import React from "react";
import { 
  User, 
  Edit2, 
  Camera, 
  Check, 
  X, 
  Trophy, 
  Mail, 
  Shield, 
  Activity, 
  Star, 
  GripVertical,
  ChevronRight
} from "lucide-react";

// ==========================================
// --- MINI MOCKUP COMPONENTS ---
// ==========================================

const ProfileHeaderMockup = () => (
  <div className="bg-white border border-gray-200 rounded-2xl p-5 flex items-center gap-4 shadow-sm relative overflow-hidden">
    <div className="absolute top-0 right-0 p-2 bg-blue-50 border-b border-l border-gray-100 rounded-bl-xl">
      <Edit2 size={14} className="text-blue-600" />
    </div>
    <div className="relative">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center text-gray-400 border-2 border-dashed border-gray-300">
        <User size={30} />
      </div>
      <div className="absolute -bottom-1 -right-1 bg-blue-600 text-white p-1 rounded-full border-2 border-white">
        <Camera size={10} />
      </div>
    </div>
    <div>
      <div className="h-5 w-32 bg-gray-800 rounded mb-2"></div>
      <div className="h-3 w-20 bg-gray-300 rounded"></div>
    </div>
  </div>
);

const InviteCardMockup = () => (
  <div className="bg-white border-l-4 border-blue-500 rounded-xl p-4 shadow-md flex justify-between items-center">
    <div>
      <h4 className="font-bold text-gray-900 text-sm italic">Elite Basketball Academy</h4>
      <p className="text-[10px] text-gray-500 font-bold uppercase tracking-tighter">New Team Invitation</p>
    </div>
    <div className="flex gap-2">
      <button className="p-2 bg-green-500 text-white rounded-lg"><Check size={14}/></button>
      <button className="p-2 bg-gray-100 text-gray-400 rounded-lg"><X size={14}/></button>
    </div>
  </div>
);

// ==========================================
// --- MAIN PAGE COMPONENT ---
// ==========================================

const ProfilePage = () => {
  return (
    <div className="space-y-12 pb-20">
      {/* Header Section */}
      <section>
        <h1 className="text-4xl font-black text-gray-900 tracking-tight mb-4">Athlete Profile</h1>
        <p className="text-lg text-gray-600 max-w-2xl leading-relaxed">
          Your Nuvio Profile is your digital athletic resume. It’s designed to showcase your 
          growth, verify your metrics, and connect you with elite coaching.
        </p>
      </section>

      {/* 1. Edit Mode & Avatar */}
      <section className="grid md:grid-cols-2 gap-8 items-start">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="bg-blue-100 p-2 rounded-lg text-blue-600"><Edit2 size={20}/></div>
            <h3 className="text-xl font-bold text-gray-900">Edit Mode & Identity</h3>
          </div>
          <p className="text-gray-600 text-sm leading-relaxed">
            By default, your profile is in <strong>View Only</strong> mode to prevent accidental changes. 
            To update your bio, height, weight, or graduation year:
          </p>
          <ul className="space-y-3">
            {[
              "Click the Pencil icon in the top right of your header.",
              "Tap your Avatar to upload a high-quality headshot.",
              "Save changes to lock your profile back down."
            ].map((step, i) => (
              <li key={i} className="flex gap-3 text-sm text-gray-700 font-medium">
                <span className="text-blue-600 font-bold">{i + 1}.</span> {step}
              </li>
            ))}
          </ul>
        </div>
        <ProfileHeaderMockup />
      </section>

      <hr className="border-gray-100" />

      {/* 2. Multi-Sport Tabs */}
      <section className="space-y-6">
        <div className="flex items-center gap-2">
          <div className="bg-orange-100 p-2 rounded-lg text-orange-600"><Trophy size={20}/></div>
          <h3 className="text-xl font-bold text-gray-900">Multi-Sport Configuration</h3>
        </div>
        <p className="text-gray-600 text-sm">
          Nuvio Sport allows you to maintain unique data sets for different sports. A "Point Guard" in Basketball 
          needs to track different metrics than a "Wide Receiver" in Football.
        </p>
        
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Toggle Sport</label>
              <div className="bg-white border border-gray-300 rounded-lg p-3 flex justify-between items-center text-sm shadow-sm cursor-pointer">
                <span className="font-bold text-gray-800">Basketball</span>
                <ChevronRight size={14} className="rotate-90" />
              </div>
            </div>
            <div className="md:col-span-2 space-y-4">
               <div className="bg-white p-4 rounded-xl border border-gray-200">
                  <h4 className="font-bold text-gray-900 text-sm mb-1">Position & Style</h4>
                  <p className="text-xs text-gray-500">Selecting your <strong>Player Type</strong> (e.g., Slasher, Sniper) helps the AI customize your drill intensity.</p>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Managing Invites */}
      <section className="grid md:grid-cols-2 gap-12">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="bg-green-100 p-2 rounded-lg text-green-600"><Mail size={20}/></div>
            <h3 className="text-xl font-bold text-gray-900">Team Invitations</h3>
          </div>
          <p className="text-gray-600 text-sm leading-relaxed">
            When a coach wants to add you to their official roster, you will receive an invite card. 
            Accepting an invite grants that coach access to your <strong>Combine Metrics</strong> 
            and allows them to leave <strong>SWOT Evaluations</strong>.
          </p>
          <div className="flex items-center gap-2 text-blue-600 font-bold text-xs bg-blue-50 p-3 rounded-lg border border-blue-100">
            <Shield size={16} />
            <span>Only accepted coaches can view your private training data.</span>
          </div>
        </div>
        <div className="flex flex-col justify-center">
          <InviteCardMockup />
        </div>
      </section>

      {/* 4. SWOT Evaluations */}
      <section className="bg-slate-900 rounded-3xl p-8 text-white">
        <div className="flex flex-col md:flex-row gap-8 items-center">
          <div className="flex-1 space-y-4">
            <div className="flex items-center gap-2">
              <Activity className="text-purple-400" />
              <h3 className="text-2xl font-bold">Coach SWOT Evaluations</h3>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              Strengths, Weaknesses, Opportunities, and Threats. These are qualitative reviews left 
              by your verified coaches.
            </p>
            <div className="flex gap-2 items-center text-xs font-bold text-yellow-400 uppercase">
              <Star size={14} fill="currentColor" />
              <span>Tip: Favorite a SWOT to prioritize it in your AI Training</span>
            </div>
          </div>

          <div className="w-full md:w-72 space-y-3">
            <div className="bg-slate-800 p-4 rounded-xl border border-slate-700">
              <div className="flex justify-between items-center mb-2">
                <span className="text-[10px] font-bold text-green-400 uppercase">Strength</span>
                <Star size={12} className="text-yellow-400" fill="currentColor" />
              </div>
              <p className="text-sm font-bold">Explosive Lateral Speed</p>
            </div>
            <div className="bg-slate-800 p-4 rounded-xl border border-slate-700 opacity-60">
              <div className="flex justify-between items-center mb-2">
                <span className="text-[10px] font-bold text-red-400 uppercase">Weakness</span>
              </div>
              <p className="text-sm font-bold">Post-Up Awareness</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProfilePage;